import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {of, Subject} from 'rxjs';
import {catchError, finalize, takeUntil, tap} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {AppState} from '../../../../core/reducers';
import {currentUser, LoginAction} from '../../../../core/auth';
import {AuthService, PushNotificationService} from '../../../../core/services';
import {AuthModel} from '../../../../core/models/auth.model';
import {SwPush} from '@angular/service-worker';
import { USER_STATUS_ENABLE } from '../../../../core/models/user.model';

/**
 * ログイン後に一時パスワードのユーザをパスワード変更ページに誘導する処理は
 * /app/core/auth/_guards/status.guard.ts にある。
 */
@Component({
    selector: 'kt-login',
    templateUrl: './login.component.html',
    encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
    loginForm?: FormGroup;

    loading = false;

    errors: any = [];

    notices = [];

    private unsubscribe: Subject<null>;

    private returnUrl: any;

    /**
     * web push通知のためのパブリックキー。
     * サーバー側にVAPID_PRIVATE_KEYがあって、合わせて使う
     */
    readonly VAPID_PUBLIC_KEY = 'BJi67ZxuXySMseUIB6kKWoyztN5bq-Hf-CRMeVMoJIrM0yreala7e2-8cKOiLWMs33O8epD28Vm-PzMpQUM8WMU';

    constructor(
        private router: Router,
        private auth: AuthService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private swPush: SwPush,
        private pushNotificationService: PushNotificationService) {
        this.unsubscribe = new Subject();
    }

    ngOnInit(): void {
        this.initLoginForm();
        const favIcon = document.querySelector('#appIcon') as HTMLLinkElement;
        favIcon.href = 'https://order-go-test.studio55.co.jp/favicon.ico';

        // redirect back to the returnUrl before login
        this.route.queryParams.subscribe(params => {
            this.returnUrl = params.returnUrl || '/';
        });
        this.loginForm?.valueChanges.subscribe((value) => {
            if (this.loginForm?.controls["password"].hasError("wrongPassword")) {
                this.loginForm.controls["password"].setErrors(null);
            }
        })
    }

    ngOnDestroy(): void {
        this.unsubscribe.next(null);
        this.unsubscribe.complete();
        this.loading = false;
    }

    initLoginForm() {
        this.loginForm = this.fb.group({
            email: ['', Validators.compose([
                Validators.required,
                Validators.email,
                Validators.minLength(3),
                Validators.maxLength(320)
            ])],
            password: ['', Validators.compose([
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100)
            ])]
        });
    }

    submit() {
        const controls = this.loginForm?.controls;
        if (controls && this.loginForm?.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()

            );
            return;
        }

        this.loading = true;

        const authData = {
            email: controls?.email.value,
            password: controls?.password.value
        };
        this.auth
            .login(authData.email, authData.password)
            .pipe(
                tap((auth: AuthModel) => {
                    if (auth) {
                        this.store.dispatch(new LoginAction({authToken: auth.access_token, pathToken: null}));
                        this.store.pipe(select(currentUser), takeUntil(this.unsubscribe)).subscribe(user => {
                            if (user === undefined) {
                                return;
                            }
                            if (user.status === USER_STATUS_ENABLE) {
                                this.router.navigateByUrl(this.returnUrl);
                            } else {
                                this.router.navigateByUrl("/auth/change-password");
                            }
                            // Web PUSHの対応 ↓
                            this.swPush.requestSubscription({
                                serverPublicKey: this.VAPID_PUBLIC_KEY
                            }).then(subscription => {
                                console.warn(subscription);
                                this.pushNotificationService.sendWebPushNotificationToBackEnd(JSON.stringify(subscription)).subscribe(() => {});
                            }).catch(err => {
                                console.error('Could not subscribe to notifications', err);
                            });
                        });

                    } else {
                        controls?.password.markAsTouched();
                        controls?.password.setErrors({wrongPassword: true});
                    }
                }),
                catchError(() => {
                    controls?.password.markAsTouched();
                    controls?.password.setErrors({wrongPassword: true});
                    return of('Error!');
                }),
                takeUntil(this.unsubscribe),
                finalize(() => {
                    this.loading = false;
                    this.cdr.markForCheck();
                })
            ).subscribe();
    }

    isControlHasError(controlName: "password" | "email", validationType: string): boolean {
        const control = this.loginForm?.controls[controlName];
        if (!control) {
            return false;
        }
        return control.errors && control.errors[validationType] && control.dirty && control.touched;
    }
}
